import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Copy, CheckCircle2, FileSpreadsheet, FileDown, Send, Clock, XCircle, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

function fmt(n) {
  if (n === 0 || n === undefined || n === null) return '0';
  return new Intl.NumberFormat('en-IN').format(Math.round(n));
}

function SlipTable({ data, tableRef }) {
  if (!data) return null;
  const m = data.monthly;
  const y = data.yearly;
  const epf = data.epf_enabled !== false;

  // Earnings rows
  const earningsRows = [
    { label: 'Basic', sub: '(50% of Gross)', m: m.basic, y: y.basic },
    { label: 'HRA', sub: '(50% of Basic)', m: m.hra, y: y.hra },
    { label: 'Retention Bonus', m: m.retention, y: y.retention },
    { label: 'PLI', sub: '(remainder)', m: m.pli, y: y.pli },
    { label: 'Annual Bonus', sub: '(yearly only)', m: null, y: y.annual_bonus || null, italic: true },
  ];
  if (epf) {
    earningsRows.push({ label: 'Gratuity', sub: '(Basic × 4.81% × 12)', m: null, y: y.gratuity, italic: true });
  }
  // Deduction rows
  const deductionsRows = epf
    ? [
        { label: 'Provident Fund (Employee)', sub: '(fixed ₹1,800)', m: m.employee_pf, y: y.employee_pf },
        { label: 'Income Tax (TDS)', m: null, y: null },
      ]
    : [{ label: 'Income Tax (TDS)', m: null, y: null }];

  // Pad to align
  const maxRows = Math.max(earningsRows.length, deductionsRows.length);
  while (earningsRows.length < maxRows) earningsRows.push({ blank: true });
  while (deductionsRows.length < maxRows) deductionsRows.push({ blank: true });

  const cellStyle = { padding: '8px 12px', borderRight: '1px solid #cbd5e1', borderBottom: '1px solid #e5e7eb', fontSize: '12.5px' };
  const cellR = { ...cellStyle, textAlign: 'right', fontFamily: 'Courier New, monospace' };
  const cellBR = { ...cellStyle, borderRight: '1.5px solid #334155' };
  const cellRBR = { ...cellR, borderRight: '1.5px solid #334155' };

  return (
    <div ref={tableRef} data-testid="salary-slip-table">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Arial, sans-serif', border: '1.5px solid #334155' }}>
        <thead>
          <tr>
            <th colSpan="6" style={{ background: '#065F46', color: 'white', padding: '12px', fontSize: '16px', fontWeight: 'bold', textAlign: 'center', borderBottom: '1.5px solid #334155' }}>
              Salary Break Up
            </th>
          </tr>
          {data.employee_name && (
            <tr>
              <td colSpan="6" style={{ padding: '8px 12px', background: '#ecfdf5', fontSize: '12.5px', borderBottom: '1.5px solid #334155' }}>
                <strong>{data.employee_name}</strong>
                {data.employee_id_code ? ` (${data.employee_id_code})` : ''}
                {data.organization_name ? ` | ${data.organization_name}` : ''}
              </td>
            </tr>
          )}
          <tr style={{ background: '#f1f5f9' }}>
            <th colSpan="3" style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 'bold', fontSize: '13px', borderRight: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>EARNINGS</th>
            <th colSpan="3" style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 'bold', fontSize: '13px', borderBottom: '1.5px solid #334155' }}>DEDUCTIONS</th>
          </tr>
          <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: '11px', fontWeight: 500 }}>
            <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 'normal', borderBottom: '1.5px solid #334155' }}>Component</th>
            <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 'normal', borderBottom: '1.5px solid #334155' }}>Monthly</th>
            <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 'normal', borderRight: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>Yearly</th>
            <th style={{ padding: '6px 12px', textAlign: 'left', fontWeight: 'normal', borderBottom: '1.5px solid #334155' }}>Component</th>
            <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 'normal', borderBottom: '1.5px solid #334155' }}>Monthly</th>
            <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 'normal', borderBottom: '1.5px solid #334155' }}>Yearly</th>
          </tr>
        </thead>
        <tbody>
          {earningsRows.map((er, idx) => {
            const dr = deductionsRows[idx] || { blank: true };
            const italicStyle = er.italic ? { fontStyle: 'italic', color: '#94a3b8' } : {};
            return (
              <tr key={idx}>
                {er.blank ? (<><td style={cellStyle}></td><td style={cellR}></td><td style={cellRBR}></td></>) : (
                  <>
                    <td style={{ ...cellStyle, ...italicStyle }}>
                      {er.label}
                      {er.sub && <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 4 }}>{er.sub}</span>}
                    </td>
                    <td style={{ ...cellR, ...italicStyle }}>{er.m === null ? '—' : fmt(er.m)}</td>
                    <td style={{ ...cellRBR, ...italicStyle }}>{er.y === null ? '—' : (er.y === 0 && er.italic ? '—' : fmt(er.y))}</td>
                  </>
                )}
                {dr.blank ? (<><td style={cellStyle}></td><td style={cellR}></td><td style={cellR}></td></>) : (
                  <>
                    <td style={cellStyle}>
                      {dr.label}
                      {dr.sub && <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 4 }}>{dr.sub}</span>}
                    </td>
                    <td style={cellR}>{dr.m === null ? '—' : fmt(dr.m)}</td>
                    <td style={cellR}>{dr.y === null ? '—' : fmt(dr.y)}</td>
                  </>
                )}
              </tr>
            );
          })}
          {/* Totals */}
          <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
            <td style={{ ...cellStyle, fontWeight: 'bold', borderTop: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>Total Earnings</td>
            <td style={{ ...cellR, fontWeight: 'bold', borderTop: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>{fmt(m.sub_total)}</td>
            <td style={{ ...cellRBR, fontWeight: 'bold', borderTop: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>{fmt(y.sub_total)}</td>
            <td style={{ ...cellStyle, fontWeight: 'bold', borderTop: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>Total Deductions</td>
            <td style={{ ...cellR, fontWeight: 'bold', borderTop: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>{fmt(m.employee_deduction)}</td>
            <td style={{ ...cellR, fontWeight: 'bold', borderTop: '1.5px solid #334155', borderBottom: '1.5px solid #334155' }}>{fmt(y.employee_deduction)}</td>
          </tr>
          {/* Employer PF strip */}
          {epf && (
            <tr style={{ background: '#eff6ff', color: '#1e40af' }}>
              <td colSpan="6" style={{ padding: '8px 14px', fontSize: '11.5px', fontStyle: 'italic', borderBottom: '1px solid #cbd5e1' }}>
                + Employer PF Contribution <span style={{ fontSize: '10px' }}>(fixed ₹1,800 · not deducted from salary; included in CTC)</span>
                <span style={{ float: 'right', fontFamily: 'Courier New, monospace', fontWeight: 600, fontStyle: 'normal' }}>
                  Monthly {fmt(m.employer_pf)} · Yearly {fmt(y.employer_pf)}
                </span>
              </td>
            </tr>
          )}
          {/* Net Pay + CTC footer */}
          <tr style={{ background: '#065F46', color: 'white' }}>
            <td colSpan="3" style={{ padding: '14px 16px', borderRight: '2px solid #064e3b', verticalAlign: 'top' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.85, letterSpacing: '0.6px', fontWeight: 600 }}>Net Final Payable</div>
              <div style={{ fontFamily: 'Courier New, monospace', fontSize: '15px', fontWeight: 'bold', marginTop: 2 }}>
                {fmt(m.net_final_payable)} <span style={{ fontSize: '11px', opacity: 0.8, fontWeight: 'normal' }}>/ mo</span>
              </div>
              <div style={{ fontFamily: 'Courier New, monospace', fontSize: '11px', opacity: 0.85 }}>{fmt(y.net_final_payable)} / yr</div>
            </td>
            <td colSpan="3" style={{ padding: '14px 16px', verticalAlign: 'top' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.85, letterSpacing: '0.6px', fontWeight: 600 }}>{epf ? 'Cost to Company' : 'Total Earnings (Gross)'}</div>
              <div style={{ fontFamily: 'Courier New, monospace', fontSize: '15px', fontWeight: 'bold', marginTop: 2 }}>
                {fmt(epf ? m.ctc : m.sub_total)} <span style={{ fontSize: '11px', opacity: 0.8, fontWeight: 'normal' }}>/ mo</span>
              </div>
              <div style={{ fontFamily: 'Courier New, monospace', fontSize: '11px', opacity: 0.85 }}>{fmt(epf ? y.ctc : y.sub_total)} / yr</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function LegalSalarySlipPage() {
  const { isAdminLevel, isLOPManager } = useAuth();
  const isLop = typeof isLOPManager === 'function' ? isLOPManager() : false;
  const [inHand, setInHand] = useState('');
  const [retention, setRetention] = useState('');
  const [annualBonus, setAnnualBonus] = useState('');
  const [empName, setEmpName] = useState('');
  const [empCode, setEmpCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [designation, setDesignation] = useState('');
  const [slipData, setSlipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const tableRef = useRef(null);

  // Load employees, orgs and verticals on mount (skip employees for LOP Manager)
  React.useEffect(() => {
    if (!isLop) {
      api.get('/employees').then(res => setEmployees(res.data)).catch(() => {});
    }
    api.get('/organizations').then(res => setOrgs(res.data)).catch(() => {});
    api.get('/verticals').then(res => setVerticals(res.data)).catch(() => {});
  }, [isLop]);

  const handleEmployeeSelect = (empId) => {
    setSelectedEmployee(empId);
    if (empId === '__manual__') {
      setEmpName('');
      setEmpCode('');
      setOrgName('');
      setDesignation('');
      setInHand('');
      setRetention('');
      return;
    }
    const emp = employees.find(e => e.id === empId);
    if (emp) {
      setEmpName(emp.name || '');
      setEmpCode(emp.employee_id || '');
      const org = orgs.find(o => o.id === emp.organization_id);
      setOrgName(org ? org.name : '');
      const vert = verticals.find(v => v.id === emp.vertical_id);
      setDesignation(vert ? vert.name : '');
      // Auto-populate salary fields
      const gross = emp.gross_salary || emp.monthly_salary || 0;
      const ret = emp.retention_amount || emp.retention_bonus || 0;
      setInHand(gross ? String(gross) : '');
      setRetention(ret ? String(ret) : '');
    }
  };

  const handleGenerate = async () => {
    if (!inHand || parseFloat(inHand) <= 0) {
      toast.error('Please enter Sub Total');
      return;
    }
    // Determine if EPF enabled for selected org
    let isEpf = true; // default for manual entry
    if (selectedEmployee && selectedEmployee !== '__manual__') {
      const emp = employees.find(e => e.id === selectedEmployee);
      if (emp) {
        const org = orgs.find(o => o.id === emp.organization_id);
        isEpf = org ? (org.epf_enabled || false) : false;
      }
    } else if (orgName) {
      const org = orgs.find(o => o.name === orgName);
      isEpf = org ? (org.epf_enabled || false) : false;
    }
    setLoading(true);
    try {
      const res = await api.post('/legal/salary-slip', {
        in_hand_salary: parseFloat(inHand),
        retention_bonus: parseFloat(retention) || 0,
        annual_bonus: parseFloat(annualBonus) || 0,
        epf_enabled: isEpf,
        employee_name: empName || null,
        employee_id_code: empCode || null,
        organization_name: orgName || null,
        designation: designation || null,
      });
      setSlipData(res.data);
    } catch (e) {
      toast.error('Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!tableRef.current) return;
    const range = document.createRange();
    range.selectNodeContents(tableRef.current);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    document.execCommand('copy');
    sel.removeAllRanges();
    setCopied(true);
    toast.success('Table copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerateDocument = async () => {
    if (!slipData) { toast.error('Generate salary slip first'); return; }
    // Determine EPF
    let isEpf = true;
    if (selectedEmployee && selectedEmployee !== '__manual__') {
      const emp = employees.find(e => e.id === selectedEmployee);
      if (emp) {
        const org = orgs.find(o => o.id === emp.organization_id);
        isEpf = org ? (org.epf_enabled || false) : false;
      }
    } else if (orgName) {
      const org = orgs.find(o => o.name === orgName);
      isEpf = org ? (org.epf_enabled || false) : false;
    }
    try {
      const response = await api.post('/legal/salary-slip/docx', {
        in_hand_salary: parseFloat(inHand),
        retention_bonus: parseFloat(retention) || 0,
        annual_bonus: parseFloat(annualBonus) || 0,
        epf_enabled: isEpf,
        employee_name: empName || null,
        employee_id_code: empCode || null,
        organization_name: orgName || null,
        designation: designation || null,
        effective_date: effectiveDate || null,
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = empName ? 'Salary_Revision_' + empName.replace(/\s+/g, '_') + '.docx' : 'Salary_Revision.docx';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (e) {
      toast.error('Failed to generate document');
    }
  };

  // --- Zoho Sign Integration (Mocked) ---
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signerEmployee, setSignerEmployee] = useState('');
  const [signerHR, setSignerHR] = useState('');
  const [signerMgmt, setSignerMgmt] = useState('');
  const [sendingSign, setSendingSign] = useState(false);
  const [signRequests, setSignRequests] = useState([]);
  const [showSignHistory, setShowSignHistory] = useState(false);

  const loadSignRequests = useCallback(async () => {
    try {
      const res = await api.get('/legal/sign-requests');
      setSignRequests(res.data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (!isLop) loadSignRequests(); }, [loadSignRequests, isLop]);

  const handleSendForSignature = async () => {
    if (!signerEmployee || !signerHR || !signerMgmt) {
      toast.error('Please fill all signer email addresses');
      return;
    }
    // Determine EPF for the chosen employee/org (same logic as Generate/Download)
    let isEpf = true;
    if (selectedEmployee && selectedEmployee !== '__manual__') {
      const emp = employees.find(e => e.id === selectedEmployee);
      if (emp) {
        const org = orgs.find(o => o.id === emp.organization_id);
        isEpf = org ? (org.epf_enabled || false) : false;
      }
    } else if (orgName) {
      const org = orgs.find(o => o.name === orgName);
      isEpf = org ? (org.epf_enabled || false) : false;
    }
    setSendingSign(true);
    try {
      await api.post('/legal/sign-requests', {
        employee_name: empName,
        employee_id_code: empCode,
        organization_name: orgName,
        designation: designation,
        in_hand_salary: parseFloat(inHand),
        retention_bonus: parseFloat(retention) || 0,
        annual_bonus: parseFloat(annualBonus) || 0,
        epf_enabled: isEpf,
        effective_date: effectiveDate,
        signer_employee: signerEmployee,
        signer_hr: signerHR,
        signer_management: signerMgmt,
      });
      toast.success('Document sent for signature via Zoho Sign');
      setSignDialogOpen(false);
      setSignerEmployee('');
      setSignerHR('');
      setSignerMgmt('');
      loadSignRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingSign(false);
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="legal-salary-slip-page" className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Salary Slip Generator</h1>
          <p className="text-muted-foreground">Generate salary breakdown from Sub Total (Monthly Gross) with EPF components</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Form */}
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="text-lg">Input Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Employee picker — hidden for LOP Manager (manual entry only) */}
              {!isLop && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Employee (optional)</Label>
                  <Select value={selectedEmployee} onValueChange={handleEmployeeSelect}>
                    <SelectTrigger data-testid="employee-select"><SelectValue placeholder="Select or enter manually" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__manual__">Enter manually</SelectItem>
                      {employees.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.employee_id} — {e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Name</Label>
                  <Input value={empName} onChange={e => setEmpName(e.target.value)} placeholder="Employee Name" data-testid="emp-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Emp ID</Label>
                  <Input value={empCode} onChange={e => setEmpCode(e.target.value)} placeholder="e.g. 1301" data-testid="emp-code-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Company</Label>
                  <Input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Company" data-testid="org-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Vertical</Label>
                  <Input value={designation} onChange={e => setDesignation(e.target.value)} placeholder="Vertical" data-testid="designation-input" />
                </div>
              </div>

              <div className="h-px bg-border" />

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Sub Total (Monthly Gross)</Label>
                <Input type="number" value={inHand} onChange={e => setInHand(e.target.value)} placeholder="e.g. 43000" className="font-mono text-lg" data-testid="in-hand-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Retention Bonus (Monthly)</Label>
                <Input type="number" value={retention} onChange={e => setRetention(e.target.value)} placeholder="e.g. 2000" className="font-mono" data-testid="retention-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Annual Bonus <span className="text-[10px] text-muted-foreground font-normal">(yearly amount, paid once)</span></Label>
                <Input type="number" value={annualBonus} onChange={e => setAnnualBonus(e.target.value)} placeholder="0" className="font-mono" data-testid="annual-bonus-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Effective Date</Label>
                <Input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} data-testid="effective-date-input" />
              </div>

              <Button onClick={handleGenerate} disabled={loading} className="w-full" data-testid="generate-slip-btn">
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {loading ? 'Generating...' : 'Generate Salary Slip'}
              </Button>
            </CardContent>
          </Card>

          {/* Output */}
          <div className="lg:col-span-2 space-y-3">
            {slipData && (
              <>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={handleCopy} data-testid="copy-table-btn">
                    {copied ? <CheckCircle2 className="h-4 w-4 mr-1.5 text-emerald-600" /> : <Copy className="h-4 w-4 mr-1.5" />}
                    {copied ? 'Copied!' : 'Copy Table'}
                  </Button>
                  <Button onClick={handleGenerateDocument} variant={isLop ? 'default' : 'outline'} data-testid="generate-doc-btn">
                    <FileDown className="h-4 w-4 mr-1.5" />
                    Download .docx
                  </Button>
                  {!isLop && (
                    <Button onClick={() => { setSignerEmployee(''); setSignDialogOpen(true); }} data-testid="send-sign-btn">
                      <PenTool className="h-4 w-4 mr-1.5" />
                      Send for Signature
                    </Button>
                  )}
                </div>
                <Card>
                  <CardContent className="p-0 overflow-x-auto">
                    <SlipTable data={slipData} tableRef={tableRef} />
                  </CardContent>
                </Card>
              </>
            )}
            {!slipData && (
              <Card>
                <CardContent className="p-16 text-center">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">Enter Sub Total and click Generate to see the salary breakdown</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Signature Requests History — hidden for LOP Manager */}
        {!isLop && signRequests.length > 0 && (
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Signature Requests</CardTitle>
              <Badge variant="outline" className="text-xs">{signRequests.length} total</Badge>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Sent To</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signRequests.map(req => (
                      <TableRow key={req.id} data-testid={`sign-req-${req.id}`}>
                        <TableCell>
                          <span className="font-medium">{req.employee_name}</span>
                          {req.employee_id_code && <span className="text-xs text-muted-foreground ml-1">({req.employee_id_code})</span>}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          <div>{req.signer_employee}</div>
                          <div>{req.signer_hr}</div>
                          <div>{req.signer_management}</div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {req.status === 'pending' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 text-xs"><Clock className="h-3 w-3 mr-1" />Pending</Badge>}
                          {req.status === 'signed' && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Signed</Badge>}
                          {req.status === 'declined' && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs"><XCircle className="h-3 w-3 mr-1" />Declined</Badge>}
                          {req.status === 'sent' && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300 text-xs"><Send className="h-3 w-3 mr-1" />Sent</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Send for Signature Dialog */}
        <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><PenTool className="h-5 w-5" />Send for Signature via Zoho Sign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                The salary revision document for <strong>{empName || 'Employee'}</strong> will be sent to the following parties for e-signature.
              </p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Employee Email</Label>
                  <Input type="email" value={signerEmployee} onChange={e => setSignerEmployee(e.target.value)} placeholder="employee@company.com" data-testid="signer-employee" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">HR Email</Label>
                  <Input type="email" value={signerHR} onChange={e => setSignerHR(e.target.value)} placeholder="hr@company.com" data-testid="signer-hr" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Management Email</Label>
                  <Input type="email" value={signerMgmt} onChange={e => setSignerMgmt(e.target.value)} placeholder="management@company.com" data-testid="signer-mgmt" />
                </div>
              </div>
              <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <strong>Note:</strong> Zoho Sign integration requires API credentials. Once configured, this will send the document via Zoho Sign for multi-party e-signature.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSendForSignature} disabled={sendingSign} data-testid="confirm-send-sign">
                <Send className="h-4 w-4 mr-1.5" />{sendingSign ? 'Sending...' : 'Send for Signature'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
